'use client';

import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Context, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Profile,
  ProfileMinQuery,
  ProfileMinQueryVariables
} from '@/__generated_graphql__/graphql';
import { BannerSlot } from '@/components/Banner/BannerSlot';
import { BurgerMenu } from '@/components/BurgerMenu/BurgerMenu';
import { ParticipantDropdown } from '@/components/ParticipantDropdown/ParticipantDropdown';
import { ParticipantTabs } from '@/components/ParticipantTabs/ParticipantTabs';
import { ProfileMenu } from '@/components/ProfileMenu/ProfileMenu';
import { ThemeSwitcher } from '@/components/ThemeSwitcher/ThemeSwitcher';
import { Button } from '@/components/ui/Button/Button';
import { BookmarkIcon, LogoIcon } from '@/components/ui/Icons/Icons';
import { BannerPlacement } from '@/constants/banners';
import {
  HEADER_AD_BUTTON_ID,
  HEADER_BOOKMARKS_BUTTON_ID,
  PAGE_HEADER_ID,
  PROFILE_MODES,
  ROUTES
} from '@/constants/common';
import { SELECT_AD_TYPE_CY } from '@/constants/test-id';
import { GET_PROFILE_MIN } from '@/graphql/queries';
import { useBelow } from '@/hooks/useBelow';
import { useLoadingEvent } from '@/hooks/useLoadingEvent';
import { useParticipant } from '@/providers/ParticipantProvider';
import { useProfileMode } from '@/providers/ProfileModeProvider/ProfileModeProvider';
import { useSelectAdTypeModal } from '@/providers/SelectAdTypeModalProvider';
import { RequiredHeaderContextProps } from '@/types';
import { ProfileFormatter } from '@/utils/helpers/profile';

import * as S from './Header.styles';

const SearchInput = dynamic(
  () =>
    import('@/components/Header/HeaderSearchInput').then(
      mod => mod.HeaderSearchInput
    ),
  { ssr: false }
);

const { HOME, LOGIN, BOOKMARKS, RESUMES } = ROUTES;

export type HeaderProps = {
  showParticipantTabs?: boolean;
  showSearch?: boolean;
  showModalSearch?: boolean;
  withBlur?: boolean;
  withProgressBar?: boolean;
  context?: Context<RequiredHeaderContextProps>;
  isSimplified?: boolean;
  hideOnMobile?: boolean;
  showTabletThemeSwitcher?: boolean;
  showTopBanner?: boolean;
};

export const Header: FC<HeaderProps> = ({
  showParticipantTabs,
  showSearch = false,
  showModalSearch = false,
  withBlur = true,
  withProgressBar = true,
  isSimplified,
  hideOnMobile,
  showTabletThemeSwitcher = false,
  showTopBanner = true,
  context
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation();
  const { isResumes, participant } = useParticipant();
  const { profileMode, switchProfileMode, canSwitchProfileMode } =
    useProfileMode();
  const { open: openEntrySelect } = useSelectAdTypeModal();

  const isBelowTabletL = useBelow('tabletL');
  const isMobile = useBelow('tablet');

  const [isScrolled, setIsScrolled] = useState(false);
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);

  const { data } = useQuery<ProfileMinQuery, ProfileMinQueryVariables>(
    GET_PROFILE_MIN
  );

  const profile: Profile | null = (data?.profile as Profile) ?? null;
  const isAuth = !!data && !!profile;

  const isSearchLoading = useLoadingEvent();

  const isShowParticipantTabs =
    showParticipantTabs !== undefined ? showParticipantTabs : !isMobile;

  const isSearchVisible = isMobile ? showModalSearch && showSearch : showSearch;

  useEffect(() => {
    if (!withBlur) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [withBlur]);

  const handleLogin = () => router.push(LOGIN);

  const handleGoToBookmarks = () => router.push(BOOKMARKS);

  const handleToggleBurger = () => setIsBurgerOpen(prev => !prev);

  const addButtonLabel = `Header.${isResumes ? 'company' : 'candidate'}_add_button_label`;

  const { name, image } = ProfileFormatter.getProfileProps(
    profile,
    profileMode
  );

  const isOnBookmarkPage = BOOKMARKS === pathname;

  const headerLink = isResumes ? RESUMES : HOME;

  return (
    <>
      {showTopBanner && (
        <BannerSlot
          placement={BannerPlacement.PageTop}
          overrides={{ bn: isBelowTabletL ? '2' : '3' }}
        />
      )}
      <S.Wrapper
        data-cy="header"
        $isBlured={isScrolled}
        $hideOnMobile={hideOnMobile}
        id={PAGE_HEADER_ID}
      >
        <S.LeftSection>
          <S.MenuAndLogo>
            {!isSimplified && (
              <BurgerMenu
                isOpen={isBurgerOpen}
                handleToggleBurger={handleToggleBurger}
              />
            )}
            <Link data-cy="header-logo" href={headerLink}>
              <LogoIcon />
            </Link>
          </S.MenuAndLogo>

          {!isSimplified && isShowParticipantTabs && (
            <>
              <S.DesktopParticipantTabs>
                <ParticipantTabs isSimplified />
              </S.DesktopParticipantTabs>
              <S.TabletParticipantDropdown>
                <ParticipantDropdown />
              </S.TabletParticipantDropdown>
            </>
          )}
        </S.LeftSection>
        <S.RightContentWrapper>
          {isSearchVisible && (
            <S.CenterSection>
              <SearchInput
                context={context as Context<RequiredHeaderContextProps>}
                placeholder={t(`Header.search_by_${participant.toLowerCase()}`)}
              />
            </S.CenterSection>
          )}

          {showTabletThemeSwitcher && (
            <S.TabletThemeSwitcher>
              <ThemeSwitcher />
            </S.TabletThemeSwitcher>
          )}

          <S.RightSection>
            <S.ButtonsAndSearchWrapper $isInvisible={!!isSimplified}>
              <S.DesktopOnlyTheme>
                <ThemeSwitcher />
              </S.DesktopOnlyTheme>

              <S.GoToBookmarksButton
                $isActive={isOnBookmarkPage}
                id={HEADER_BOOKMARKS_BUTTON_ID}
                aria-label="Go to bookmarks vacancies or resumes"
                data-cy="header-bookmarks-button"
                onClick={handleGoToBookmarks}
              >
                <BookmarkIcon />
              </S.GoToBookmarksButton>

              <Button
                size="m"
                onClick={openEntrySelect}
                id={HEADER_AD_BUTTON_ID}
                data-cy={SELECT_AD_TYPE_CY.OPEN_MODAL_BTN}
              >
                <S.FullAddLabel variant="title4">
                  {t(addButtonLabel)}
                </S.FullAddLabel>
                <S.ShortAddLabel variant="title4">
                  {t('Header.add_button_short_label')}
                </S.ShortAddLabel>
              </Button>
            </S.ButtonsAndSearchWrapper>

            {isAuth ? (
              <ProfileMenu
                email={profile.email}
                name={name}
                profileMode={profileMode ?? PROFILE_MODES.COMPANY}
                image={image}
                onSwitchProfileMode={switchProfileMode}
                canSwitchProfileMode={canSwitchProfileMode}
              />
            ) : (
              <S.LoginButtonWrapper>
                <Button size="m" variant="outlined" onClick={handleLogin}>
                  {t('Header.login_button_label')}
                </Button>
              </S.LoginButtonWrapper>
            )}
          </S.RightSection>
        </S.RightContentWrapper>
        {withProgressBar && <S.ProgressBar $isLoading={isSearchLoading} />}
      </S.Wrapper>
    </>
  );
};
